// Single adapter boundary for the alerts contract: `data` is a list of { message, priority }.

export const DEFAULT_PRIORITY = 3;

const VALID_PRIORITIES = new Set([1, 2, 3]);

// Fills stay the bus table's; the rule and text tones follow next-ride's banner.
export const PRIORITY_CONFIG = {
  1: {
    fill: "#ffe6e6",
    rule: "#fecaca",
    text: "#b91c1c",
    iconColor: "#b91c1c",
    icon: "triangle",
    // Matches the table's bold value cells (index.css `table tbody`).
    fontSize: "0.95rem",
    fontWeight: 700,
    iconSize: 24,
    srLabel: "Urgent alert",
  },
  2: {
    fill: "#ffebcc",
    rule: "#fde68a",
    text: "#b45309",
    iconColor: "#b45309",
    icon: "triangle",
    fontSize: "0.9em",
    fontWeight: 500,
    iconSize: 20,
    srLabel: "Important alert",
  },
  3: {
    fill: "transparent",
    rule: "#e5e7eb",
    text: "#374151",
    iconColor: "#6b7280",
    icon: "bullet",
    fontSize: "0.9em",
    fontWeight: 500,
    iconSize: 20,
    srLabel: "Informational alert",
  },
};

export const getPriorityConfig = (priority) =>
  PRIORITY_CONFIG[priority] || PRIORITY_CONFIG[DEFAULT_PRIORITY];

/**
 * Stable djb2 content hash. Used only for React keys and new-P1 detection.
 * @param {string} value - The content to hash.
 * @returns {string} A base-36 hash of the supplied content.
 */
export const contentHash = (value) => {
  const text = String(value);
  let hash = 5381;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) + hash + text.codePointAt(index)) >>> 0;
  }
  return hash.toString(36);
};

// The API sends "1"; anything outside 1–3 falls back to informational rather than dropping.
const toPriority = (value) => {
  const numeric = typeof value === "string" ? Number(value.trim()) : value;
  return VALID_PRIORITIES.has(numeric) ? numeric : DEFAULT_PRIORITY;
};

/**
 * Normalizes the raw API payload into the shape the banner renders, in API order.
 * @param {unknown} input - The raw payload, expected to be an array.
 * @returns {Array<{id: string, message: string, priority: number}>} Normalized messages, possibly empty.
 */
export const normalizeMessages = (input) => {
  if (!Array.isArray(input)) return [];

  const seen = new Set();
  return input.reduce((accumulator, raw) => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      return accumulator;
    }
    const message = typeof raw.message === "string" ? raw.message.trim() : "";
    if (!message) return accumulator;

    const priority = toPriority(raw.priority);
    // Priority is in the hash so a P2 escalated to P1 counts as a new P1.
    const id = contentHash(`${message}|${priority}`);
    if (seen.has(id)) return accumulator;
    seen.add(id);

    accumulator.push({ id, message, priority });
    return accumulator;
  }, []);
};

/**
 * Sorts messages by priority; Array#sort is stable, so ties keep their order.
 * @param {Array<object>} messages - Normalized messages.
 * @returns {Array<object>} A new, sorted array.
 */
export const sortMessages = (messages) =>
  [...(messages || [])].sort((first, second) => first.priority - second.priority);
